import type { LanguageCode, Translatable } from '@/types'

/**
 * The partner directory and everything derived from it.
 *
 * The whole directory arrives with the page, so narrowing it is plain data
 * work. These mirror src/utils/hvc.js in the mobile app, so both read the
 * same directory the same way.
 */

export interface PartnerType {
  id: number
  name: Translatable<string>
  icon: string | null
}

export interface PartnerBranch {
  id: number
  address: Translatable<string>
  contact: string | null
  province: { code: number | string; name: Translatable<string> } | null
  district: { code: number | string; name: Translatable<string> } | null
  position: { lat: number; lng: number } | null
}

export interface Partner {
  id: number
  name: Translatable<string>
  conditions: Translatable<string>
  contact: string | null
  discount_percentage: number
  icon: string | null
  is_logo: boolean
  type: PartnerType | null
  branches: PartnerBranch[]
}

export type Province = NonNullable<PartnerBranch['province']>

/**
 * A bundle the panel has reserved for card holders, in My Roshan's own bundle
 * shape — a field holds its translations, or `false` when the bundle has none.
 */

/**
 * The store the subscriber is sent to. The website never activates a bundle —
 * the same rule the plan cards already follow for a `myroshan` plan.
 */
export function storeLink(): string {
  const agent = typeof navigator === 'undefined' ? '' : navigator.userAgent.toLowerCase()

  return /iphone|ipad|ipod|mac/.test(agent)
    ? 'https://apps.apple.com/pk/app/my-roshan/id1512175766'
    : 'https://play.google.com/store/apps/details?id=af.roshan.myroshan'
}

export const SORT_DISCOUNT = 'discount'
export const SORT_ALPHABETICAL = 'alphabetical'

export type PartnerSort = typeof SORT_DISCOUNT | typeof SORT_ALPHABETICAL

export interface PartnerFilters {
  typeId?: number | null
  provinceCode?: number | string | null
}

/** Codes arrive as numbers from the API and as strings from a select. */
function sameCode(
  one: number | string | null | undefined,
  two: number | string | null | undefined
): boolean {
  return one != null && two != null && String(one) === String(two)
}

function localized(
  translations: Translatable<string> | null | undefined,
  locale: LanguageCode
): string {
  return translations?.[locale] ?? translations?.en ?? ''
}

/**
 * The branch a partner is shown by.
 *
 * A partner may trade in several places and the row has room for one, so it is
 * the branch in the province being filtered to, and its first otherwise.
 */
export function branchFor(
  partner: Partner,
  provinceCode: number | string | null = null
): PartnerBranch | null {
  const branches = partner?.branches ?? []

  return (
    branches.find((branch) => sameCode(branch.province?.code, provinceCode)) ?? branches[0] ?? null
  )
}

/** A partner is in a province when any of its branches is. */
export function isInProvince(partner: Partner, provinceCode: number | string): boolean {
  return (partner?.branches ?? []).some((branch) => sameCode(branch.province?.code, provinceCode))
}

export function filterPartners(
  partners: Partner[],
  { typeId = null, provinceCode = null }: PartnerFilters = {}
): Partner[] {
  return partners.filter((partner) => {
    if (typeId && partner.type?.id !== typeId) {
      return false
    }

    if (provinceCode && !isInProvince(partner, provinceCode)) {
      return false
    }

    return true
  })
}

/** Highest discount first, or by name in whichever language is being read. */
export function sortPartners(
  partners: Partner[],
  sort: PartnerSort,
  locale: LanguageCode
): Partner[] {
  const ordered = [...partners]

  return sort === SORT_ALPHABETICAL
    ? ordered.sort((one, two) =>
        localized(one.name, locale).localeCompare(localized(two.name, locale))
      )
    : ordered.sort((one, two) => two.discount_percentage - one.discount_percentage)
}

/**
 * The provinces a filter can offer, derived from the branches in hand rather
 * than served on their own, so the filter can only offer a province that has
 * a partner in it.
 */
export function provincesOf(partners: Partner[]): Province[] {
  const provinces = new Map<number | string, Province>()

  for (const partner of partners) {
    for (const branch of partner.branches ?? []) {
      if (branch.province?.code && !provinces.has(branch.province.code)) {
        provinces.set(branch.province.code, branch.province)
      }
    }
  }

  return [...provinces.values()]
}
